import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { OnboardingImportOutcomes, type OnboardingImportReport, type OnboardingImportTenant } from "@nexus/contracts";
import { Badge, Button, cn, Icon, SearchableCombobox } from "@nexus/ui";
import { SettingRow, SettingsCard } from "@/features/changelog/components/shared/SettingsCard";
import type { OnboardingImportRunState } from "@/features/admin/api/onboarding-import.api";
import { ApiError } from "@/lib/api-client";
import {
  onboardingImportKeys,
  onboardingImportOverviewQueryOptions,
  onboardingImportRunQueryOptions,
  useStartOnboardingImport,
} from "@/lib/queries/onboarding-import.queries";

const OUTCOME_BADGE = {
  [OnboardingImportOutcomes.SUCCESS]: "status-connected",
  [OnboardingImportOutcomes.PARTIAL]: "status-in-production",
  [OnboardingImportOutcomes.FAILED]: "status-error",
} as const;

/**
 * Admin → Settings → System, "Import onboarding data from BQ": refreshes the selected tenants' Nexus
 * onboarding records from BigQuery. One run at a time; the server refuses a second start.
 */
export function OnboardingImportCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const overview = useQuery(onboardingImportOverviewQueryOptions());
  const start = useStartOnboardingImport();
  const [selected, setSelected] = useState<string[]>([]);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const run = useQuery(onboardingImportRunQueryOptions(correlationId));

  const activeRun = overview.data?.activeRun ?? null;
  useEffect(() => {
    if (activeRun?.isOwnRun && correlationId === null) setCorrelationId(activeRun.correlationId);
  }, [activeRun, correlationId]);

  const runPhase = run.data?.phase;
  useEffect(() => {
    if (runPhase === "done" || runPhase === "failed") {
      void queryClient.invalidateQueries({ queryKey: onboardingImportKeys.overview() });
    }
  }, [runPhase, queryClient]);

  const tenants = overview.data?.tenants ?? [];
  const nameById = new Map(tenants.map((tenant) => [tenant.id, tenant.name]));
  const isOwnRunActive = correlationId !== null && (run.isPending || runPhase === "running") && !run.isError;
  const isOtherRunActive = activeRun !== null && !activeRun.isOwnRun;
  const isBusy = start.isPending || isOwnRunActive || isOtherRunActive;
  const isAllSelected = tenants.length > 0 && selected.length === tenants.length;

  function toggleTenant(tenant: OnboardingImportTenant) {
    setSelected((current) =>
      current.includes(tenant.id) ? current.filter((id) => id !== tenant.id) : [...current, tenant.id],
    );
  }

  async function handleSync() {
    if (selected.length === 0 || isBusy) return;
    setStartError(null);
    try {
      const { correlationId: started } = await start.mutateAsync(selected);
      setCorrelationId(started);
    } catch (error) {
      setStartError(toStartError(error, t));
      void queryClient.invalidateQueries({ queryKey: onboardingImportKeys.overview() });
    }
  }

  return (
    <SettingsCard
      icon="download"
      title={t("admin.systemSettings.onboardingImport.title", "Import onboarding data from BQ")}
      description={t(
        "admin.systemSettings.onboardingImport.description",
        "Refreshes the selected tenants' onboarding records from the onboarding BigQuery tables. BigQuery is only read; nothing is written to it.",
      )}
    >
      <SettingRow
        label={t("admin.systemSettings.onboardingImport.tenantsLabel", "Tenants")}
        description={t(
          "admin.systemSettings.onboardingImport.tenantsHint",
          "Live organizations with a customer partition.",
        )}
        control={
          <div className="flex items-center gap-2">
            <SearchableCombobox<OnboardingImportTenant>
              items={tenants}
              values={selected}
              onSelect={toggleTenant}
              getKey={(tenant) => tenant.id}
              getLabel={(tenant) => tenant.name}
              getSearchText={(tenant) => `${tenant.name} ${tenant.customerPartition}`}
              getSelectedLabel={(items) =>
                items.length === 1
                  ? items[0]!.name
                  : t("admin.systemSettings.onboardingImport.selectedCount", "{{count}} tenants selected", {
                      count: items.length,
                    })
              }
              placeholder={t("admin.systemSettings.onboardingImport.placeholder", "Select tenants")}
              ariaLabel={t("admin.systemSettings.onboardingImport.tenantsLabel", "Tenants")}
              searchPlaceholder={t("admin.systemSettings.onboardingImport.search", "Search tenants…")}
              emptyMessage={t("admin.systemSettings.onboardingImport.empty", "No eligible tenants")}
              loading={overview.isLoading}
              error={overview.isError ? t("admin.systemSettings.onboardingImport.loadError", "Could not load tenants.") : null}
              disabled={isBusy}
              closeOnSelect={false}
              triggerClassName="h-8 w-[18rem] max-w-full text-xs"
              footer={
                <div className="flex justify-between gap-2 border-t border-border p-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={tenants.length === 0 || isAllSelected}
                    onClick={() => setSelected(tenants.map((tenant) => tenant.id))}
                  >
                    {t("admin.systemSettings.onboardingImport.selectAll", "Select all eligible tenants ({{count}})", {
                      count: tenants.length,
                    })}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={selected.length === 0}
                    onClick={() => setSelected([])}
                  >
                    {t("admin.systemSettings.onboardingImport.clear", "Clear")}
                  </Button>
                </div>
              }
            />
            <Button type="button" disabled={selected.length === 0 || isBusy} onClick={() => void handleSync()}>
              {isBusy
                ? t("admin.systemSettings.onboardingImport.syncing", "Syncing…")
                : t("admin.systemSettings.onboardingImport.sync", "Sync")}
            </Button>
          </div>
        }
      />
      <RunStatus
        state={run.data ?? null}
        isStarting={start.isPending || (correlationId !== null && run.isPending)}
        isLost={run.isError}
        isOtherRunActive={isOtherRunActive}
        startError={startError}
        nameById={nameById}
      />
    </SettingsCard>
  );
}

function RunStatus({
  state,
  isStarting,
  isLost,
  isOtherRunActive,
  startError,
  nameById,
}: {
  state: OnboardingImportRunState | null;
  isStarting: boolean;
  isLost: boolean;
  isOtherRunActive: boolean;
  startError: string | null;
  nameById: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation();
  if (startError) return <StatusLine tone="error">{startError}</StatusLine>;
  if (isLost) {
    return (
      <StatusLine tone="error">
        {t(
          "admin.systemSettings.onboardingImport.lost",
          "Lost track of this import. Its outcome is in Admin Events.",
        )}
      </StatusLine>
    );
  }
  if (isStarting) {
    return <StatusLine isBusy>{t("admin.systemSettings.onboardingImport.starting", "Starting the import…")}</StatusLine>;
  }
  if (state?.phase === "running") {
    return (
      <StatusLine isBusy>
        {state.progress
          ? t("admin.systemSettings.onboardingImport.progress", "Synced {{done}} of {{total}} tenants…", state.progress)
          : t("admin.systemSettings.onboardingImport.queued", "Waiting for the import to start…")}
      </StatusLine>
    );
  }
  if (state?.phase === "failed") {
    return (
      <StatusLine tone="error">
        {state.message ||
          t("admin.systemSettings.onboardingImport.runFailed", "The import could not finish. Nothing more was synced.")}
      </StatusLine>
    );
  }
  if (state?.phase === "done") return <ImportReport report={state.report} nameById={nameById} />;
  if (isOtherRunActive) {
    return (
      <StatusLine isBusy>
        {t("admin.systemSettings.onboardingImport.otherRun", "Another admin's import is running. Try again when it finishes.")}
      </StatusLine>
    );
  }
  return null;
}

function ImportReport({ report, nameById }: { report: OnboardingImportReport; nameById: ReadonlyMap<string, string> }) {
  const { t } = useTranslation();
  const failed = report.tenants.filter((tenant) => tenant.status === "failed");
  const outcomeLabel = {
    [OnboardingImportOutcomes.SUCCESS]: t("admin.systemSettings.onboardingImport.outcome.success", "Success"),
    [OnboardingImportOutcomes.PARTIAL]: t("admin.systemSettings.onboardingImport.outcome.partial", "Partial success"),
    [OnboardingImportOutcomes.FAILED]: t("admin.systemSettings.onboardingImport.outcome.failed", "Failed"),
  }[report.outcome];

  return (
    <div role="status" className="flex flex-col gap-2 px-5 py-3.5 text-xs">
      <div className="flex items-center gap-2">
        <Badge variant={OUTCOME_BADGE[report.outcome]}>{outcomeLabel}</Badge>
        <span className="text-foreground">
          {t("admin.systemSettings.onboardingImport.summary", "{{synced}} of {{total}} tenants synced", report)}
        </span>
      </div>
      {failed.length > 0 && (
        <ul
          aria-label={t("admin.systemSettings.onboardingImport.failedList", "Tenants that did not sync")}
          className="m-0 flex list-none flex-col gap-1 p-0"
        >
          {failed.map((tenant) => (
            <li key={tenant.tenantId} className="text-muted-foreground">
              <span className="font-medium text-foreground">{nameById.get(tenant.tenantId) ?? tenant.tenantId}</span>
              {" · "}
              <span className="font-mono">{tenant.reason}</span>
              {" · "}
              {tenant.detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusLine({ children, tone, isBusy = false }: { children: string; tone?: "error"; isBusy?: boolean }) {
  return (
    <p
      role="status"
      className={cn(
        "m-0 flex items-center gap-2 px-5 py-3.5 text-xs",
        tone === "error" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {isBusy && <Icon name="loader" size="sm" color="inherit" className="animate-spin" aria-hidden />}
      {children}
    </p>
  );
}

function toStartError(error: unknown, t: ReturnType<typeof useTranslation>["t"]): string {
  if (error instanceof ApiError && error.status === 409) {
    return t("admin.systemSettings.onboardingImport.alreadyRunning", "An import is already running. Wait for it to finish.");
  }
  if (error instanceof ApiError && error.status === 400) {
    return t(
      "admin.systemSettings.onboardingImport.notEligible",
      "Some selected tenants can no longer be imported. Reload the page and select again.",
    );
  }
  return t("admin.systemSettings.onboardingImport.startError", "Could not start the import. Try again.");
}
